/* Supply Chain — a faithful React port of the prototype's rSupply screen.
 * Choose a supplier per component (chips/display/battery/sensors) plus a final
 * assembly partner; cheaper suppliers cut cost but trade away quality, lead time
 * and reliability. An animated world map shows where the chosen chain sits and
 * routes parts → assembly → your paid markets, and a combined-reliability card
 * multiplies the per-supplier reliabilities into the odds of a clean quarter.
 *
 * Selections are staged in the decisions draft (store.setSupplier → d.suppliers)
 * and previewed live here; the server's applyDecisions replays the same intent. */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { useMemo } from 'react';
import { Engine, SUPPLIERS, COUNTRIES } from '@venturewrist/engine';
import { SUPPLIER_CATS, type SupplierCat } from '../../lib/constants';
import { useDecisions } from '../../store/decisions';
import { buildWorkingGame } from '../../lib/designState';
import SupplyMapView from '../../components/SupplyMapView';
import type { MapData } from '../../components/supplyMap';
import type { ScreenProps } from './types';
import '../../styles/supply-chain.css';

const Eng = Engine as any;
const SUPS = SUPPLIERS as unknown as Record<string, any[]>;

/* prototype card order + labels for rSupply's `cats` array */
const CATS: [SupplierCat, string][] = [
  ['chips', 'Processor & chips'],
  ['display', 'Displays'],
  ['battery', 'Batteries'],
  ['sensors', 'Sensors'],
  ['assembly', 'Final assembly'],
];

/* country flags — Windows can't render flag emoji, so use flagcdn images keyed
 * by ISO alpha-2, mirroring the prototype's flagName()/iso2() helpers. */
const NAME2CC: Record<string, string> = (() => {
  const m: Record<string, string> = {};
  for (const c of COUNTRIES as any[]) m[c.name] = c.id;
  m['Hungary'] = 'hu';
  return m;
})();
const ISO2_FIX: Record<string, string> = { uk: 'gb', idn: 'id', sau: 'sa' };
const iso2 = (cc: string) => { cc = String(cc || '').toLowerCase(); return ISO2_FIX[cc] || cc; };
function Flag({ country }: { country: string }) {
  const code = iso2(NAME2CC[country] || '');
  if (!code) return null;
  return (
    <img className="flag" src={`https://flagcdn.com/${code}.svg`} alt={country} title={country} loading="lazy"
      onError={(e) => { (e.currentTarget as HTMLImageElement).replaceWith(document.createTextNode(code.toUpperCase() + ' ')); }} />
  );
}

const relClass = (rel: number): string => (rel >= 0.95 ? 'good' : rel >= 0.9 ? 'warn' : 'bad');
const WhyChip = () => <span className="tooltip-why" title="Learn the theory">?</span>;

export function SupplyChain({ detail }: ScreenProps) {
  const draft = useDecisions((z) => z.draft);
  const setSupplier = useDecisions((z) => z.setSupplier);
  // The working game carries every staged decision (suppliers here, but also
  // markets entered on Distribution), so the map shows what submit will do.
  const s = useMemo(() => buildWorkingGame(detail.state, draft) as any, [detail.state, draft]);

  const chosenId = (cat: SupplierCat): string => s.suppliers?.[cat];

  // combined chain reliability = product of the five chosen suppliers' rel
  const rel = useMemo(() => {
    return SUPPLIER_CATS.reduce((a, cat) => {
      const sup = Eng.sup(cat, chosenId(cat));
      return a * (sup?.rel ?? 1);
    }, 1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [s]);
  const relPct = Math.round(rel * 100);
  const relColor = rel >= 0.85 ? 'var(--good)' : rel >= 0.72 ? 'var(--warn)' : 'var(--bad)';

  // map data — reflects the staged selection so the preview matches submit
  const mapData: MapData = useMemo(() => {
    const sup = (cat: SupplierCat) => Eng.sup(cat, chosenId(cat));
    return {
      suppliers: (['chips', 'display', 'battery', 'sensors'] as SupplierCat[]).map(sup),
      assembly: sup('assembly'),
      countries: COUNTRIES as any[],
      markets: s.markets ?? {},
      qDemand: (c: any) => Eng.qDemand(c, s.round ?? 1),
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [s]);

  return (
    <div className="sc-root">
      <div className="screen-head">
        <h2>🌍 Global Supply Chain<WhyChip /></h2>
        <div className="sub">Choose suppliers per component. Cheaper isn't free: quality, lead time and reliability differ. The map legend names each kind of marker: component suppliers, final assembly, and your markets.</div>
      </div>

      <SupplyMapView data={mapData} />

      <div className="grid3" style={{ gridTemplateColumns: 'repeat(2,1fr)' }}>
        {CATS.map(([cat, label]) => (
          <div className="card" key={cat}>
            <h3>{label}</h3>
            <div className="sup-list">
              {(SUPS[cat] ?? []).map((sup) => {
                const on = chosenId(cat) === sup.id;
                return (
                  <div key={sup.id} className={'sup-row' + (on ? ' on' : '')} onClick={() => setSupplier(cat, sup.id)}>
                    <div>
                      <div>
                        <b>{sup.name}</b>{' '}
                        <span className="sub">· <Flag country={sup.country} />{sup.country}</span>
                      </div>
                      <div className="sub" style={{ marginTop: 2 }}>
                        cost ×{sup.costM.toFixed(2)} · quality ×{sup.qualM.toFixed(2)} · lead {sup.lead}wk
                      </div>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <span className={'chip ' + relClass(sup.rel)}>{Math.round(sup.rel * 100)}% reliable</span>
                      {sup.capacity ? <div className="sub" style={{ marginTop: 3 }}>cap {sup.capacity}k/qtr</div> : null}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      <div className="card" style={{ marginTop: 14 }}>
        <div className="spread">
          <div>
            <b>Combined chain reliability: <span style={{ color: relColor }}>{relPct}%</span></b>
            <div className="sub">Probability of a normal quarter without shipment shortfall. A shortfall cuts production 12–28%.</div>
          </div>
          <WhyChip />
        </div>
      </div>
    </div>
  );
}
